import { TrendingDown, Users, Award, ShieldCheck, BookOpen, Receipt } from "lucide-react";

// Default 3-card configuration — Remote Teams positioning.
const REMOTE_CARDS = [
    {
        id: "cost",
        icon: TrendingDown,
        chip: "60%",
        chipLabel: "avg. cost reduction",
        eyebrow: "Cost Efficiency",
        title: "Up to 60% savings. Zero compromise on quality.",
        body: "Reduce operational costs dramatically compared to local European or US hiring — while retaining the same professional output, compliance standards and communication quality your business demands.",
    },
    {
        id: "skilled",
        icon: Users,
        chip: "100%",
        chipLabel: "professionally qualified",
        eyebrow: "Skilled Workforce",
        title: "Qualified professionals.",
        body: "Finance specialists, compliance experts, operations managers and digital support professionals — all trained to international standards and experienced with European and US clients.",
    },
    {
        id: "intl",
        icon: Award,
        chips: ["Netherlands", "Germany", "UK", "United States"],
        eyebrow: "EU & US Client Experience",
        title: "Proven track record with international clients.",
        body: "Hands-on experience with SMEs across the Netherlands, Germany, the UK and accounting firms across the US. We understand cross-border compliance, multi-currency operations and the communication standards international clients expect.",
    },
];

// US Bookkeeping configuration.
const US_BOOKKEEPING_CARDS = [
    {
        id: "big4",
        icon: ShieldCheck,
        chips: ["PwC", "KPMG"],
        eyebrow: "Big-4 audit discipline",
        title: "Audit-grade books, every month.",
        body: "Our founders trained inside PwC and KPMG global audit practices. That discipline shows up in every reconciliation, every accrual and every close — quietly, every month.",
    },
    {
        id: "gaap",
        icon: BookOpen,
        chips: ["ASC 606", "ASC 842"],
        eyebrow: "US GAAP fluency",
        title: "US GAAP, applied with care.",
        body: "Revenue recognition (ASC 606), leases (ASC 842), deferred revenue, multi-entity rollups — handled by accountants who speak GAAP and your CPA firm’s language.",
    },
    {
        id: "scale",
        icon: Receipt,
        chip: "3+ years",
        chipLabel: "of specialized US GAAP experience",
        eyebrow: "Tax-season scalability",
        title: "Built for founders who’d rather not think about bookkeeping.",
        body: "We flex up around 1099s, year-ends and tax season so your CPA receives clean, audit-ready books — and you never feel the wave.",
    },
];

export default function WhyVittaniti({
    kicker = "Why Vittaniti",
    title,
    intro,
    cards = REMOTE_CARDS,
    testid = "why-vittaniti",
}) {
    const defaultTitle = (
        <>Not just outsourcing.<br /><span className="italic text-brand-amber">A true</span> business extension.</>
    );
    const defaultIntro =
        "We become part of your team — aligned to your goals, your culture, and your way of working. Your offshore office in Hyderabad, operating as if we're down the hall.";

    return (
        <section className="section container-page" data-testid={testid}>
            <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 mb-14 items-end">
                <div className="lg:col-span-7">
                    <p className="overline mb-3">{kicker}</p>
                    <h2 className="display text-4xl md:text-5xl lg:text-6xl text-brand-ink leading-[1.05]">
                        {title || defaultTitle}
                    </h2>
                </div>
                <div className="lg:col-span-5">
                    <p className="text-brand-ink/75 leading-relaxed">{intro || defaultIntro}</p>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {cards.map((c, i) => {
                    const Icon = c.icon;
                    return (
                        <article
                            key={c.id}
                            data-testid={`${testid}-card-${c.id}`}
                            className="card-3d p-7 md:p-8 flex flex-col h-full animate-fade-up"
                            style={{ animationDelay: `${i * 90}ms` }}
                        >
                            <div className="flex items-start justify-between mb-6">
                                <span className="grid place-items-center w-12 h-12 rounded-xl bg-brand-creamLight text-brand-ink">
                                    <Icon size={22} strokeWidth={1.6} />
                                </span>
                                {c.chip && (
                                    <div className="text-right">
                                        <p className="font-serif text-3xl leading-none text-brand-amber">{c.chip}</p>
                                        <p className="text-[10px] uppercase tracking-[0.18em] mt-1 text-brand-muted">
                                            {c.chipLabel}
                                        </p>
                                    </div>
                                )}
                            </div>
                            <p className="overline mb-2 text-brand-muted">{c.eyebrow}</p>
                            <h3 className="font-serif text-2xl text-brand-ink leading-tight">{c.title}</h3>
                            <p className="mt-4 text-sm text-brand-ink/75 leading-relaxed flex-1">{c.body}</p>
                            {c.chips && (
                                <div className="mt-6 flex flex-wrap gap-2">
                                    {c.chips.map(tag => (
                                        <span
                                            key={tag}
                                            className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium bg-brand-creamLight text-brand-ink border border-brand-border"
                                        >
                                            <span className="inline-block w-1.5 h-1.5 rounded-full bg-brand-amber" />
                                            {tag}
                                        </span>
                                    ))}
                                </div>
                            )}
                        </article>
                    );
                })}
            </div>
        </section>
    );
}

// Exposed presets so pages can pick the right card set.
WhyVittaniti.REMOTE_CARDS = REMOTE_CARDS;
WhyVittaniti.US_BOOKKEEPING_CARDS = US_BOOKKEEPING_CARDS;
